import { Injectable } from '@angular/core';
import { AccountService } from '../../User/account.service';
import { UserInstanceService } from '../../User/userInstance.service';
import { FeatureFlagsService } from '../FeatureFlags/feature-flags-service';
import { Roles } from '../Permissions/restrict.service';

import moment from 'moment';

@Injectable({
    providedIn: 'root',
})
export class ProductAnalyticsService {
    constructor(
        private readonly _accountService: AccountService,
        private readonly _featureFlagsService: FeatureFlagsService,
        private readonly _userInstanceService: UserInstanceService,
    ) {}

    injectPendo(pendoApiKey: string): void {
        const pendo = (window['pendo'] = window['pendo'] || {});
        pendo._q = pendo._q || [];
        const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
        for (let i = 0, x = methods.length; i < x; ++i) {
            const method = methods[i];
            pendo[method] =
                pendo[method] ||
                function (...args) {
                    pendo._q[method === methods[0] ? 'unshift' : 'push']([method].concat([].slice.call(args, 0)));
                };
        }
        const script = document.createElement('script');
        script.async = !0;
        script.src = `https://cdn.pendo.io/agent/static/${pendoApiKey}/pendo.js`;
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode.insertBefore(script, firstScript);
    }

    initPendo(): void {
        const envConfig = (window as any)?.weissmanEnvironmentConfig;
        if (!envConfig?.pendoApiKey) {
            console.log('Pendo is not configured for this environment.');
            return;
        }

        const userType = this._getUserType();
        const userAge = this._getUserAge();
        const selectedInstance = this._userInstanceService.getSelectedInstance();
        const IAMUserId = this._getIAMUserId();

        window['pendo'].initialize({
            visitor: {
                id: IAMUserId, // Required if user is logged in
                email: this._accountService.userData.email, // Recommended if using Pendo Feedback, or NPS Email
                full_name: `${this._accountService.userData.firstName} ${this._accountService.userData.lastName}`, // Recommended if using Pendo Feedback
                // role:         // Optional

                // You can add any additional visitor level key-values here,
                // as long as it's not one of the above reserved names.
                pp_email: this._accountService.userData.email,
                pp_full_name: `${this._accountService.userData.firstName} ${this._accountService.userData.lastName}`,
                pp_first_name: this._accountService.userData.firstName,
                pp_last_name: this._accountService.userData.lastName,
                pp_userType: userType,
                pp_createDate: this._accountService.userData.createDate,
                pp_userAge: userAge,
                pp_userTenant: selectedInstance?.name,
                pp_defaultTeam: this._accountService.userData.defaultTeam,
            },

            account: {
                id: userType, // Highly recommended, required if using Pendo Feedback or OEM Adopt
                // name:         // Optional
                // is_paying:    // Recommended if using Pendo Feedback
                // monthly_value:// Recommended if using Pendo Feedback
                // planLevel:    // Optional
                // planPrice:    // Optional
                // creationDate: // Optional

                // You can add any additional account level key-values here,
                // as long as it's not one of the above reserved names.
                pp_iamTenantId: selectedInstance?.instanceId,
                pp_iamTenantName: selectedInstance?.name,
            },
        });
    }

    private _getUserType(): string {
        const hasRyanInstance = this._accountService.userData.instanceMembership.some((x) => x.name === 'Ryan');
        const hasRyanPrivate = this._accountService.userData.globalRoles.some(
            (x) => x === Roles.RYANPRIVATEITEMSVIEW || x === Roles.RYANPRIVATEITEMSEDIT,
        );
        if (hasRyanInstance && hasRyanPrivate) {
            return 'Internal';
        } else if (hasRyanInstance && !hasRyanPrivate) {
            return 'External (read only)';
        } else {
            return 'Licensed';
        }
    }

    private _getUserAge(): string {
        const days = moment().diff(this._accountService.userData.createDate, 'days');
        const years = moment().diff(this._accountService.userData.createDate, 'years');
        if (days < 365) {
            return 'Less than 1 year';
        } else if (years >= 5) {
            return '5+ years';
        } else {
            return `${years} ${years === 1 ? 'year' : 'years'}`;
        }
    }

    private _getIAMUserId(): string {
        if (this._featureFlagsService.featureFlags.enableIAM && this._accountService.userData?.iamId) {
            return this._accountService.userData.iamId;
        }
        return this._accountService.userData.email;
    }
}
